import { billingApi, cacheTimer, thunks } from "@app/api";
import { selectEnv } from "@app/config";
import { mdw, select } from "@app/fx";
import { createSelector } from "@app/fx";
import { defaultHalHref } from "@app/hal";
import { schema } from "@app/schema";
import type {
  BillingContact,
  BillingDetail,
  ExternalPaymentSource,
  HalEmbedded,
  LinkResponse,
  StripeSource,
} from "@app/types";
import { loadStripe } from "@stripe/stripe-js/pure";

export interface StripeSourceResponse {
  id: string;
  deactivated_at: string | null;
  description: string;
  stripe_token_id: string;
  stripe_type: string;
  stripe_metadata: Record<string, any> | null;
}

export interface ExternalPaymentSourceResponse {
  id: string;
  description: string;
}

export interface BillingContactResponse {
  id: string;
  name: string;
  email: string;
}

export type {
  BillingContact,
  ExternalPaymentSource,
  StripeSource,
} from "@app/types";

const normalizeString = (value: unknown): string =>
  value == null ? "" : String(value);

const normalizeOptionalString = (value: unknown): string | undefined =>
  value == null ? undefined : String(value);

export const deserializeStripeSource = (
  r: StripeSourceResponse,
): StripeSource => ({
  id: String(r.id),
  deactivatedAt: r.deactivated_at,
  description: r.description,
  stripeTokenId: r.stripe_token_id,
  stripeType: normalizeString(r.stripe_type),
  stripeMetadata: r.stripe_metadata
    ? {
        name: normalizeOptionalString(r.stripe_metadata.name),
        last4: normalizeOptionalString(r.stripe_metadata.last4),
        expMonth: r.stripe_metadata.exp_month,
        expYear: r.stripe_metadata.exp_year,
        bankName: normalizeOptionalString(r.stripe_metadata.bank_name),
        status: normalizeOptionalString(r.stripe_metadata.status),
        setupIntentId: normalizeOptionalString(
          r.stripe_metadata.setup_intent_id,
        ),
        hostedVerificationUrl: normalizeOptionalString(
          r.stripe_metadata.hosted_verification_url,
        ),
      }
    : null,
});

const LEGACY_ACH = ["bank_account", "bank-account", "bankaccount"];
const MODERN_ACH = ["usbankaccount", "us_bank_account", "us-bank-account"];

export const isLegacyBankAccount = (source: StripeSource): boolean =>
  LEGACY_ACH.includes(normalizeString(source.stripeType).toLowerCase());

export const isModernBankAccount = (source: StripeSource): boolean =>
  MODERN_ACH.includes(normalizeString(source.stripeType).toLowerCase());

export const isBankAccount = (source: StripeSource): boolean =>
  isLegacyBankAccount(source) || isModernBankAccount(source);

export const isVerified = (source: StripeSource): boolean =>
  source.stripeMetadata?.status === "verified";

export const deserializeExternalPaymentSource = (
  source: ExternalPaymentSourceResponse,
): ExternalPaymentSource => ({
  id: String(source.id),
  description: source.description,
});

export const deserializeBillingContact = (
  contact: BillingContactResponse,
): BillingContact => ({
  id: String(contact.id),
  name: contact.name,
  email: contact.email,
});

export interface TrialResponse {
  id: string;
  range_begin: string;
  range_end: string;
}

export interface BillingAddressForm {
  streetOne: string;
  streetTwo: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
}

const ZIPCODE_REGEX = /^[0-9]{5}(?:-[0-9]{4})?$/;

export function validateBillingAddressForm(form: BillingAddressForm) {
  if (
    !form.streetOne.trim() ||
    !form.city.trim() ||
    !form.state.trim() ||
    !form.postCode.trim() ||
    !form.country.trim()
  ) {
    return "Please fill in all required fields.";
  }

  if (form.country === "US" && !ZIPCODE_REGEX.test(form.postCode.trim())) {
    return "Invalid Zip Code";
  }

  if (["US", "CA"].includes(form.country) && form.state === "NA") {
    return "State is required";
  }
}

export const defaultBillingDetailResponse = (
  bt: Partial<BillingDetailResponse> = {},
): BillingDetailResponse => {
  return {
    id: "",
    organization_details_json: {},
    invoiced_externally: false,
    external_invoice_portal_url: "",
    _links: { payment_method: defaultHalHref() },
    ...bt,
  };
};

interface BillingDetailResponse {
  id: string;
  organization_details_json: { [key: string]: any } | null;
  invoiced_externally: boolean;
  external_invoice_portal_url: string;
  _links: {
    payment_method: LinkResponse;
  };
}

const deserializeBillingDetail = (bt: BillingDetailResponse): BillingDetail => {
  return {
    id: bt.id,
    organizationDetailsJson: bt.organization_details_json || {},
    paymentMethodUrl: bt._links.payment_method
      ? bt._links.payment_method.href
      : "",
    invoicedExternally: bt.invoiced_externally || false,
    externalInvoicePortalUrl: bt.external_invoice_portal_url || "",
  };
};

export const selectBillingDetail = schema.billingDetail.select;
export const selectHasPaymentMethod = createSelector(
  selectBillingDetail,
  (bt) => bt.paymentMethodUrl !== "",
);
export const selectExternalInvoicePortalUrl = createSelector(
  selectBillingDetail,
  (bt) => bt.externalInvoicePortalUrl,
);
export const selectIsInvoicedExternally = createSelector(
  selectBillingDetail,
  (bt) => bt.invoicedExternally,
);

export const fetchBillingDetail = billingApi.get<
  { id: string },
  BillingDetailResponse
>("/billing_details/:id", function* (ctx, next) {
  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  yield* schema.update(
    schema.billingDetail.set(deserializeBillingDetail(ctx.json.value)),
  );
});

export async function getStripe(stripePublishableKey: string) {
  loadStripe.setLoadParameters({ advancedFraudSignals: false });
  return loadStripe(stripePublishableKey);
}

export const stripeClient = {
  getStripe,
};

interface AddCreditCardPayload {
  id: string;
  stripeTokenId: string;
  address1: string;
  address2: string;
  city: string;
  zipcode: string;
  state: string;
  country: string;
}

export const addCreditCard = thunks.create<AddCreditCardPayload>(
  "add-credit-card",
  [
    mdw.loader(schema),
    function* (ctx, next) {
      const config = yield* select(selectEnv);
      const ssCtx = yield* createStripeSource.run(ctx.payload);
      if (!ssCtx.json.ok) {
        throw ssCtx.json.error;
      }
      const updatePaymentCtx = yield* updateBillingDetail.run({
        id: ctx.payload.id,
        paymentMethodUrl: `${config.billingUrl}/stripe_sources/${ssCtx.json.value.id}`,
        address1: ctx.payload.address1,
        address2: ctx.payload.address2,
        city: ctx.payload.city,
        state: ctx.payload.state,
        country: ctx.payload.country,
        zipcode: ctx.payload.zipcode,
      });
      if (!updatePaymentCtx.json.ok) {
        throw updatePaymentCtx.json.error;
      }
      yield* schema.update(
        schema.loaders.success({
          id: ctx.key,
          message: "Successfully added stripe payment method!",
        }),
      );
      yield* next();
    },
  ],
);

export interface AchSetupIntentResponse {
  id: string;
  client_secret: string;
}

export const createAchSetupIntent = billingApi.post<
  { id: string },
  AchSetupIntentResponse
>("/billing_details/:id/ach_setup_intents", function* (ctx, next) {
  ctx.request = ctx.req({ body: JSON.stringify({}) });
  yield* next();
  if (!ctx.json.ok) {
    return;
  }
  ctx.loader = {
    meta: {
      clientSecret: ctx.json.value.client_secret,
    },
  };
});

export const finalizeAchSetupIntent = billingApi.post<
  { id: string; setupIntentId: string },
  StripeSourceResponse
>(
  "/billing_details/:id/ach_setup_intents/:setupIntentId/finalize",
  function* (ctx, next) {
    ctx.request = ctx.req({ body: JSON.stringify({}) });
    yield* next();
    if (!ctx.json.ok) {
      return;
    }
    // A still-pending finalize changed nothing the user can see, so it gets no
    // success banner.
    if (ctx.json.value.stripe_metadata?.status === "verified") {
      ctx.loader = { message: "Bank account verified" };
    }
  },
);

export const createStripeSource = billingApi.post<
  { id: string; stripeTokenId: string },
  StripeSourceResponse
>("/billing_details/:id/stripe_sources", function* (ctx, next) {
  const body = {
    stripe_token_id: ctx.payload.stripeTokenId,
  };
  ctx.request = ctx.req({
    body: JSON.stringify(body),
  });
  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  ctx.loader = { message: "Successfully added stripe payment method!" };
});

export const fetchStripeSources = billingApi.get<
  { id: string },
  HalEmbedded<{ stripe_sources: StripeSourceResponse[] }>
>("/billing_details/:id/stripe_sources", billingApi.cache());

export const fetchExternalPaymentSources = billingApi.get<
  { id: string },
  HalEmbedded<{ external_payment_sources: ExternalPaymentSourceResponse[] }>
>("/billing_details/:id/external_payment_sources", billingApi.cache());

export const fetchBillingContacts = billingApi.get<
  { id: string },
  HalEmbedded<{ billing_contacts: BillingContactResponse[] }>
>("/billing_details/:id/billing_contacts", billingApi.cache());

export const createBillingContact = billingApi.post<
  { id: string; name: string; email: string },
  BillingContactResponse
>("/billing_details/:id/billing_contacts", function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify({
      name: ctx.payload.name,
      email: ctx.payload.email,
    }),
  });
  yield* next();
  if (!ctx.json.ok) {
    return;
  }
  ctx.loader = { message: "Billing contact added" };
});

export const deleteBillingContact = billingApi.delete<
  { id: string },
  { id: string }
>("/billing_contacts/:id", function* (ctx, next) {
  yield* next();
  if (!ctx.json.ok) {
    return;
  }
  ctx.loader = { message: "Billing contact removed" };
});

export const deactivateStripeSource = billingApi.put<
  { id: string },
  StripeSourceResponse
>("/stripe_sources/:id", function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify({
      deactivated_at: new Date().toISOString(),
    }),
  });
  yield* next();
  if (!ctx.json.ok) {
    return;
  }
  ctx.loader = { message: "Payment method removed" };
});

export const createVerificationAttempt = billingApi.post<
  { stripeSourceId: string; amountDecimal1: string; amountDecimal2: string },
  { id: string }
>(
  "/stripe_sources/:stripeSourceId/verification_attempts",
  function* (ctx, next) {
    ctx.request = ctx.req({
      body: JSON.stringify({
        amount_decimal1: ctx.payload.amountDecimal1,
        amount_decimal2: ctx.payload.amountDecimal2,
      }),
    });
    yield* next();
    if (!ctx.json.ok) {
      return;
    }
    ctx.loader = { message: "Bank account verified" };
  },
);

export const updateDefaultPaymentMethod = thunks.create<{
  billingDetailId: string;
  paymentMethodUrl: string;
}>("update-default-payment-method", [
  mdw.loader(schema),
  function* (ctx, next) {
    const bd = yield* select(selectBillingDetail);
    const updateCtx = yield* updateBillingDetail.run({
      id: ctx.payload.billingDetailId,
      paymentMethodUrl: ctx.payload.paymentMethodUrl,
    });
    if (!updateCtx.json.ok) {
      throw updateCtx.json.error;
    }
    yield* schema.update(
      schema.billingDetail.set({
        ...bd,
        paymentMethodUrl: ctx.payload.paymentMethodUrl,
      }),
    );
    yield* next();
  },
]);

export const fetchTrials = billingApi.get<
  { id: string },
  HalEmbedded<{ trials: TrialResponse[] }>
>(
  "/billing_details/:id/trials",
  { supervisor: cacheTimer() },
  billingApi.cache(),
);

export const createBillingDetail = billingApi.post<{
  orgId: string;
  orgName: string;
}>("/billing_details", function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify({
      id: ctx.payload.orgId,
      organization_details_json: { name: ctx.payload.orgName },
    }),
  });

  yield* next();
});

interface UpdateBillingDetailProps {
  id: string;
  paymentMethodUrl?: string;
  address1?: string;
  address2?: string;
  city?: string;
  zipcode?: string;
  state?: string;
  country?: string;
}

export const updateBillingDetail = billingApi.patch<UpdateBillingDetailProps>(
  "/billing_details/:id",
  function* (ctx, next) {
    const bd = yield* select(selectBillingDetail);
    const hasAddressPayload =
      ctx.payload.address1 !== undefined ||
      ctx.payload.address2 !== undefined ||
      ctx.payload.city !== undefined ||
      ctx.payload.state !== undefined ||
      ctx.payload.zipcode !== undefined ||
      ctx.payload.country !== undefined;
    const existingAddress =
      (bd.organizationDetailsJson?.billing_address as Record<string, string>) ||
      {};
    const requestBody: Record<string, unknown> = {
      organization_details_json: hasAddressPayload
        ? {
            ...bd.organizationDetailsJson,
            billing_address: {
              street_one:
                ctx.payload.address1 ?? existingAddress.street_one ?? "",
              street_two:
                ctx.payload.address2 ?? existingAddress.street_two ?? "",
              city: ctx.payload.city ?? existingAddress.city ?? "",
              state: ctx.payload.state ?? existingAddress.state ?? "",
              post_code: ctx.payload.zipcode ?? existingAddress.post_code ?? "",
              country: ctx.payload.country ?? existingAddress.country ?? "",
            },
          }
        : bd.organizationDetailsJson,
    };
    if (ctx.payload.paymentMethodUrl !== undefined) {
      requestBody.payment_method = ctx.payload.paymentMethodUrl;
    }
    ctx.request = ctx.req({
      body: JSON.stringify(requestBody),
    });
    yield* next();
  },
);

export const updateBillingAddress = thunks.create<
  Omit<UpdateBillingDetailProps, "id" | "paymentMethodUrl"> & {
    id: string;
  }
>("update-billing-address", [
  mdw.loader(schema),
  function* (ctx, next) {
    const updateCtx = yield* updateBillingDetail.run({
      id: ctx.payload.id,
      address1: ctx.payload.address1,
      address2: ctx.payload.address2,
      city: ctx.payload.city,
      state: ctx.payload.state,
      zipcode: ctx.payload.zipcode,
      country: ctx.payload.country,
    });
    if (!updateCtx.json.ok) {
      throw updateCtx.json.error;
    }
    const detailCtx = yield* fetchBillingDetail.run({ id: ctx.payload.id });
    if (!detailCtx.json.ok) {
      throw detailCtx.json.error;
    }
    ctx.loader = { message: "Billing address updated" };
    yield* next();
  },
]);
